import axios from 'axios';
import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { Link } from 'react-router-dom';
import { Setting, Subscription } from '../../App';
import { CUSTOMER_API_URL } from '../../constants';
import { useMetafieldData } from '../../hooks/use-metafield-data';
import { useSubscriptionDetail } from '../../hooks/use-subscription-detail';
import { Product, useSubscriptionLineEditProduct } from '../../hooks/use-subscription-line-edit-product';
import { SpinButton } from '../SpinButton';

import MicroModal from 'micromodal';
import base from '../../styles/Base.module.css';
import style from '../../styles/SubscriptionDetail.module.css';

type TargetProduct = {
  index: number;
  productId: string;
  quantity: number;
  price: number;
};

type RegistrableVariantViewType = {
  productId: string;
  productTitle: string;
  variantId: string;
  variantTitle: string;
  price: number;
  originalPrice: number;
  sku: string;
  image?: string;
  url: string;
  message?: string;
  quantity: number;
  currentPriceAmount: number;
  onlineStorePreviewUrl: string;
  variantImage: string;
  subscriptionFirstPrice?: string;
  subscriptionSecondPrice?: string;
  subscriptionThirdPrice?: string;
  subscriptionFourthPrice?: string;
  subscriptionFifthPrice?: string;
};

export const AddProducts: FC<{
  subscription: Subscription;
  setting: Setting;
}> = ({ subscription, setting }) => {
  const [targetProduct, setTargetProduct] = useState<TargetProduct[]>([]);
  const [targetProductDetail, setTargetProductDetail] = useState<any>(null);
  const [productDetailModalDisplay, setProductDetailModalDisplay] = useState(false);
  const [modalMainImage, setModalMainImage] = useState<string>('');
  const [scrollPosition, setScrollPosition] = useState('');
  const [heavyLoading, setHeavyLoading] = useState(false);
  const { getRegistrableVariants } = useMetafieldData();
  const [allProducts, setAllProducts] = useState<Product[]>();
  const [isLargeWindowSize, setIsLargeWindowSize] = useState<boolean>();

  // 追加予定リスト
  const [toBeAddedVariants, setToBeAddedVariants] = useState<RegistrableVariantViewType[]>([]);
  const [productDatas, setProductDatas] = useState<any[]>([]);

  let hasExecuted = false;

  let alternativeProducts = [];

  const { planGroups } = useSubscriptionDetail(setting);

  const {
    subscriptionContractId,
    billingPolicyInterval,
    billingPolicyIntervalCount,
    billingPolicyMinCycles,
    nextBillingDate,
    // customerId,
    status,
    deliveryPriceAmount,
    deliveryFormattedAddress,
    paymentType,
    deliveryPriceCurrencyCode,
  } = subscription;

  const failureFlagRef = useRef(false);

  const {
    products,
    // allProducts
  } = useSubscriptionLineEditProduct(setting);

  // 定期登録済みの金額と数量をフッターに表示するために送る
  useEffect(() => {
    window.addEventListener('message', (e) => {
      if (e.data.windownnerWidth) {
        setIsLargeWindowSize(e.data.windownnerWidth > 999);
      }
    });
    window.parent.postMessage({ action: 'renderSubcription' }, '*');

    const deliveryAmount = deliveryPriceAmount < 5000 ? deliveryPriceAmount : 0;

    const currentPrice = subscription.subscriptionLines.reduce((totalPrice, line) => {
      return totalPrice + line.currentPriceAmount * line.quantity;
    }, deliveryAmount);

    const toBeAddedTotalPrice = targetProduct.reduce((total, product) => {
      if (!product.price) return total;
      const price = product.price * product.quantity;
      return total + price;
    }, 0);

    window.parent.postMessage(
      {
        action: 'setTotal',
        totalPrice: (currentPrice + toBeAddedTotalPrice).toLocaleString(),
        targetProduct: targetProduct,
      },
      '*',
    );

    window.removeEventListener('message', addRecommendProduct);
    if (targetProduct.length > 0) {
      window.addEventListener('message', addRecommendProduct);
    }
    // currentPrice をステートに保存する処理
    // 例: setCurrentPrice(currentPrice);
  }, [targetProduct]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // console.log(event.data.action)
      if (event.data.targetId) {
        scrollToElement(event.data.targetId);
      }
    };
    const handleScroll = () => {
      // 現在のスクロール位置にある要素の ID を取得
      const currentElementId = getCurrentElementId();
      console.log('Current Element ID:', currentElementId); // デバッグ用
      setScrollPosition(currentElementId);

      // 親ウィンドウに ID を送信
      window.parent.postMessage(
        {
          action: 'setScrollPosition',
          targetId: currentElementId,
        },
        '*',
      );
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('scroll', handleScroll);
    };
  });

  useEffect(() => {
    getAllProducts();
  }, [subscription]);

  const getAllProducts = async () => {
    if (!subscription) return;

    let line = subscription.subscriptionLines[0];
    let currentPage = 0;
    const new_products = [];

    // ページが存在する間、商品を取得
    while (true) {
      // console.log('getAllProducts : ' + currentPage);
      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getAlternativeProducts`,
          {
            page: currentPage,
            sellingPlanId: line.sellingPlanId,
          },
          {
            headers: { Authorization: setting.token },
          },
        );

        // レスポンスから商品を取得
        new_products.push(...response.data.products);

        // allProductsを都度更新
        setAllProducts([...new_products]);
        // console.log('new_products', new_products)

        // 次のページが存在しない場合、ループを終了
        if (response.data.hasNextPage === false) {
          break;
        }

        currentPage++;
      } catch (e) {
        console.log((e as Error).message);
        break;
      }
    }
  };

  useEffect(() => {
    if (!allProducts) return;
    getProductData();
  }, [allProducts]);

  useLayoutEffect(() => {
    const height = document.querySelector('body')!.scrollHeight + 100; // buffer;

    setting.resize(height);
    setting.monitorScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, allProducts, productDatas]);

  const scrollToElement = (targetId: string) => {
    // 要素が見つかるまでの最大試行回数と間隔を設定
    const maxAttempts = 10;
    const interval = 500; // ミリ秒

    // console.log({targetId});

    let attempts = 0;
    const intervalId = setInterval(() => {
      const element = document.querySelector(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        clearInterval(intervalId);
      } else if (attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
      attempts++;
    }, interval);
  };

  const getCurrentElementId = () => {
    let closestElementId = '';
    let closestDistanceToCenter = Number.MAX_VALUE;
    const viewportCenter = window.innerHeight / 2;

    document.querySelectorAll('[id]').forEach((element) => {
      const rect = element.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2;
      const distanceToCenter = Math.abs(viewportCenter - elementCenter);

      if (distanceToCenter < closestDistanceToCenter) {
        closestDistanceToCenter = distanceToCenter;
        closestElementId = element.id;
      }
    });

    return closestElementId;
  };

  // 商品追加
  const addRecommendProduct = useCallback(
    async (e: MessageEvent<any>) => {
      // 変更を保存ボタンが押されたとき
      if (e.data.action && e.data.action === 'addSubmit') {
        // setHeavyLoading(true);
        // setLoading(true);

        let firstExecutionFlag = true;
        let delay = 0;

        const planId = subscription.subscriptionLines[0].sellingPlanId;
        let sellingPlanName = '';

        // console.log({planGroups});
        planGroups.forEach((planGroup) => {
          const foundPlan = planGroup.plans.find((plan) => plan.planId === planId);
          if (foundPlan) {
            sellingPlanName = foundPlan.name;
          }
        });

        if (e.data.targetProduct && e.data.targetProduct.length > 0) {
          if (!hasExecuted && !heavyLoading) {
            hasExecuted = true;
            setHeavyLoading(true);
            // console.log('e.data.targetProduct', e.data.targetProduct);

            for (let i = 0; i < e.data.targetProduct.length; i++) {
              const data = e.data.targetProduct[i];

              if (data.quantity === 0) {
                continue;
              }

              // sellingPlanNameをループ内でコピー
              const planName = sellingPlanName;

              setTimeout(async () => {
                const variantId = data.variants[0].id
                  ? `gid://shopify/ProductVariant/${data.variants[0].id}`
                  : data.variants[0].variantId;
                const variantPrice = data.variants[0].price ? data.variants[0].price : data.price;
                const variantSku = data.sku;
                const title = data.variants[0].title ? data.variants[0].title : data.title;

                try {
                  const response = await axios.post(
                    `${CUSTOMER_API_URL}?action=addSubscriptionLine`,
                    {
                      subscriptionId: subscription.subscriptionContractId,
                      planId: planId,
                      planName: planName,
                      subscriptionLine: {
                        productId: data.productId,
                        variantId: variantId,
                        title: data.title,
                        quantity: Number(data.quantity),
                        currentPriceAmount: variantPrice,
                        lineId: null,
                        onlineStorePreviewUrl: data.onlineStorePreviewUrl,
                        variantImage: null,
                        variantTitle: title,
                        currentPriceCurrencyCode: 'JPY',
                        lineDiscountedPriceAmount: variantPrice,
                        lineDiscountedPriceCurrencyCode: 'JPY',
                        sku: variantSku,
                      },
                    },
                    {
                      headers: { Authorization: setting.token },
                    },
                  );
                  console.log({ response });
                  // resolve(response);
                } catch (error) {
                  console.error(error);
                  // resolve(null);
                  failureFlagRef.current = true;
                }
              }, i * 2000);
            }

            // 実行にかかる時間を算出（各配列の要素×2秒）
            const totalDelay = e.data.targetProduct.length * 2000;

            // 実行にかかる時間を待ってメッセージ表示
            setTimeout(() => {
              setHeavyLoading(false);
              if (failureFlagRef.current) {
                // 失敗したAPIがあった時
                MicroModal.show('modal-complete-add-error');
              } else {
                // 全部更新成功したとき
                MicroModal.show('modal-complete-add');
              }
            }, totalDelay);
          }
        }
      }
    },
    [subscription, planGroups],
  );

  const getProductData = async () => {
    alternativeProducts = allProducts!
      .filter((product) => {
        // 商品のバリエーション全てが定期購買に登録済み
        if (
          product.variants.every((variant) =>
            subscription.subscriptionLines.some((line) => line.variantId === variant.variantId),
          )
        )
          return false;

        // 商品のバリエーション全てが、配送あり定期購買で配送なし商品、または配送なし定期購買で配送あり商品
        if (
          product.variants.every(
            (variant) =>
              (variant.requiresShipping && !subscription.deliveryCountryCode) ||
              (!variant.requiresShipping && !!subscription.deliveryCountryCode),
          )
        )
          return false;

        return true;
      })
      .map((product) => {
        return {
          ...product,
          subscribed: false,
          quantity: 0,
        };
      });

    const displayedProducts = await Promise.all(
      alternativeProducts.map(async (dp: any) => {
        // const productInStaticData = productsData.find(p =>
        //   dp.onlineStorePreviewUrl.includes(p.handle.toString())
        // );
        const addInStaticData = await getRegistrableVariants(dp.productId);

        // console.log({ addInStaticData });

        if (addInStaticData && addInStaticData.productId) {
          return {
            ...dp,
            // ...productInStaticData,
            ...addInStaticData,
          };
        }
        return {
          ...dp,
        };
      }),
    );
    setProductDatas(displayedProducts);
  };

  if (!subscription) {
    return <></>;
  }

  if (!allProducts || !products) {
    return (
      <div className={style.productAdd}>
        <p>追加商品を読み込み中です・・少々お待ちください。</p>
      </div>
    );
  }

  // TODO
  const deliveryPriceCurrency = deliveryPriceCurrencyCode === 'JPY' ? '￥' : deliveryPriceCurrencyCode;

  const originQuantity = 0;

  // 数量変更
  const onChangeItemQuantity = async (pidx: any, product: any, value: number) => {
    console.log('onChangeItemQuantity', pidx, product, value);

    const target = {
      index: pidx,
      ...product,
      quantity: value,
    };

    // すでに登録済みの商品なら、数量変更
    if (targetProduct.find((tp) => tp.productId === product.productId)) {
      await setTargetProduct((prev) =>
        prev.map((tp) => {
          if (tp.productId === product.productId) {
            return target;
          }
          return tp;
        }),
      );
    } else {
      // 未登録の商品なら、追加
      await setTargetProduct((prev) => [...prev, target]);
    }
  };

  // console.log('allProducts', allProducts);

  alternativeProducts = allProducts!
    .filter((product) => {
      // 商品のバリエーション全てが定期購買に登録済み
      if (
        product.variants.every((variant) => subscription.subscriptionLines.some((line) => line.variantId === variant.variantId))
      )
        return false;

      // 商品のバリエーション全てが、配送あり定期購買で配送なし商品、または配送なし定期購買で配送あり商品
      if (
        product.variants.every(
          (variant) =>
            (variant.requiresShipping && !subscription.deliveryCountryCode) ||
            (!variant.requiresShipping && !!subscription.deliveryCountryCode),
        )
      )
        return false;

      return true;
    })
    .map((product) => {
      return {
        ...product,
        subscribed: false,
        quantity: 0,
      };
    });

  const displayedProducts = [...alternativeProducts].map((dp: any) => {
    const validProducts = productDatas.filter((p) => p.handle);
    const addInStaticData = validProducts.find((p) => dp.onlineStorePreviewUrl.includes(p.handle.toString()));
    return {
      ...dp,
      // ...productInStaticData,
      ...addInStaticData,
    };
  });

  const categories = [
    {
      title: '新商品',
      anker_id: 'new',
      products: [],
    },
    {
      title: 'ご飯もの',
      anker_id: 'rice',
      products: [],
    },
    {
      title: '麺',
      anker_id: 'noodle',
      products: [],
    },
    {
      title: 'ベーカリー',
      anker_id: 'bakery',
      products: [],
    },
    {
      title: 'デザート',
      anker_id: 'dessert',
      products: [],
    },
  ];

  const categoryProducts: any = {};
  categories.forEach((category) => {
    categoryProducts[category.anker_id] = [];
  });

  displayedProducts.forEach((product: any) => {
    // product.tags にカテゴリー名のどれかが入っていれば、そのカテゴリーに追加
    if (product.tags) {
      const isActiveProduct = product.tags.every((tag: string) => tag !== 'マイページAdditional_Order非表示');
      if (isActiveProduct) {
        product.tags.forEach((tag: any) => {
          const category = categories.find((c) => c.title === tag);
          if (category) {
            categoryProducts[category.anker_id].push(product);
          }
        });
      }
    }
  });

  const handleImageClick = async (product: Product) => {
    // console.log('product', product);
    const product_handle = product.onlineStorePreviewUrl?.slice(-11);
    // console.log('product_handle', product_handle);
    // console.log('productsData', productsData);
    // const target_product = productsData.find(p => product.onlineStorePreviewUrl?.includes(p.url));
    const target_product = productDatas.find((p) => product.onlineStorePreviewUrl?.includes(p.onlineStorePreviewUrl));
    // console.log('target_product', target_product);
    if (target_product === undefined) return;
    // setProductDetailModalDisplay(true);
    setTargetProductDetail({
      ...target_product,
      onlineStorePreviewUrl: product.onlineStorePreviewUrl,
    });
    // setModalMainImage(target_product!.featured_image);

    // window.parentMessage で商品情報を送る
    window.parent.postMessage(
      {
        action: 'setProductDetail',
        product: target_product,
      },
      '*',
    );
  };

  // 定期便の内容を確認するボタン
  // -------------------------------------------
  const handleConfirmSubscription = () => {
    // 親ウィンドウに ID を送信
    window.parent.postMessage(
      {
        action: 'scrollToSubscription',
      },
      '*',
    );
  };

  return (
    <div className={style.productAdd}>
      <div className={`${style.productHeader}`}>
        <h2 className={`${base.pageHeading} ${base.pageLargeHeading}` && isLargeWindowSize ? base.orderPc : base.orderSp}>
          Additional order
          <span className={isLargeWindowSize ? base.large : base.subOrder}>追加注文</span>
        </h2>
      </div>
      <div className={`${base.productList} product`}>
        {categoryProducts &&
          Object.keys(categoryProducts).map((categoryKey: any) => {
            // console.log({categoryKey});
            const category = categories.find((c) => c.anker_id === categoryKey);
            const shownProducts = categoryProducts[categoryKey];
            if (shownProducts.length === 0) return <></>;
            return (
              <div className={style.productAddCategory}>
                <h3 className={`${base.sectionHeading}`} id={category?.anker_id}>
                  {category?.title}
                </h3>
                {shownProducts.length > 0 &&
                  shownProducts.map((product: any, pidx: number) => {
                    return (
                      <article key={pidx} className={`${base.productItem} ${style.productItemAdd}`}>
                        <div className={style.productSummary}>
                          <div className={style.productImageTitleBlock}>
                            {/* 画像 */}
                            <figure className={base.productItemFigure} onClick={() => handleImageClick(product)}>
                              <img
                                className={base.productImage}
                                src={product.images.length > 0 ? product.images[0] : ''}
                                alt=""
                              />
                            </figure>
                            <div className={style.productDetail}>
                              <p className={style.productTitle}>{product.title}</p>
                              {/* 価格 */}
                              <div className={style.productDetailPrice}>
                                <div className={base.productItemPrice}>
                                  <span className={base.productItemPriceSubscription}>
                                    <NumberFormat
                                      displayType="text"
                                      value={Math.round(product.price * 0.95)}
                                      thousandSeparator={true}
                                      prefix={deliveryPriceCurrency}
                                    />
                                  </span>
                                </div>
                              </div>
                              <div className={style.productQuantityBlock}>
                                <div className={style.productQuantity}>
                                  <SpinButton
                                    min={0}
                                    value={targetProduct.find((tp) => tp.productId === product.productId)?.quantity || 0}
                                    onChange={(e) => {
                                      onChangeItemQuantity(pidx, product, e);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
              </div>
            );
          })}
      </div>

      {/* 追加完了モーダル
      -------------------------------------------*/}
      <div className="micromodal" id={`modal-complete-add`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-coupon-title"
          >
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={base.ModalTitle}>
                定期便の内容を更新しました
              </h2>
            </header>
            <main id="modal-coupon-content" className={base.ModalHeader}>
              <div className={`${base.ModalFooter} ${base.ModalFooterSmallLink}`} style={{ textAlign: 'center' }}>
                <Link to={`/`} onClick={() => handleConfirmSubscription()}>
                  定期便の内容を確認する
                </Link>
              </div>
            </main>
          </div>
        </div>
      </div>
      <div className="micromodal" id={`modal-complete-add-error`} aria-hidden="true">
        <div className={`${base.micromodalOverlay} micromodal-overlay`} tabIndex={-1} data-micromodal-close>
          <div
            role="dialog"
            className={`${base.micromodalOverlayDialog} ${style.OtherMenuModal}`}
            aria-modal="true"
            aria-labelledby="modal-coupon-title"
          >
            <header className={base.ModalHeader}>
              <button aria-label="Close modal" className={base.closeModal} data-micromodal-close></button>
              <h2 id="modal-coupon-title" className={base.ModalTitle}>
                エラーが発生しました。 <br />
                店舗へお問い合わせください。
              </h2>
            </header>
            <main id="modal-coupon-content" className={base.ModalHeader}>
              <div className={`${base.ModalFooter}`}>
                {/* <Link to={`/subscriptions/${encodeURIComponent(subscriptionContractId)}/`} className={style.otherMenuCancelModalLink}>定期便の内容を確認する</Link> */}
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* 商品詳細モーダル */}
      {productDetailModalDisplay && (
        <>
          <div className={base.modalCover} onClick={() => setProductDetailModalDisplay(false)}></div>
          <div className={base.modal}>
            <h2 className={base.modalTitle}>{targetProductDetail && targetProductDetail.title} の詳細</h2>
            <div className={base.modalContent}>
              {/* 画像 */}
              <div className={base.modalContentLeft}>
                <div className={base.modalImageMain}>
                  <img className={base.modalImageMainImage} src={modalMainImage} />
                </div>
                <div className={base.modalImageList}>
                  {targetProductDetail &&
                    targetProductDetail.media.map((m: any, index: number) => (
                      <div
                        key={`preview_image_${index}`}
                        className={`${base.modalImageItem} ${modalMainImage === m.src ? base.modalImageItem__active : ''}`}
                      >
                        <img src={m.preview_image.src} alt="" className={base.modalImageItemImage} />
                      </div>
                    ))}
                </div>
              </div>

              {/* 商品詳細 */}
              <div className={base.modalContentRight}>
                {targetProductDetail && (
                  <div>
                    <div className={base.modalContentTitle}>{targetProductDetail.title}</div>
                    <div className={base.modalContentDesc}>{targetProductDetail.description}</div>
                    <div className={base.modalContentPrice}>
                      <p className={`${base.modalContentPriceLine} ${base.modalContentPriceLine__right}`}>
                        定期定期便価格:
                        <NumberFormat
                          displayType="text"
                          value={Math.round((targetProductDetail.price / 100) * 0.95)}
                          thousandSeparator={true}
                          prefix={deliveryPriceCurrency}
                          className={base.red}
                        />
                        <span className={base.red}>-</span>
                      </p>
                    </div>

                    {targetProductDetail && (
                      <div className={base.modalFooter}>
                        <button className={base.sectionBtn} onClick={() => setProductDetailModalDisplay(false)}>
                          戻る
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
